import { Box, Button, LoadingOverlay, Modal, Select, Text, Tooltip, UnstyledButton, useForm, LoadError } from "@dehub/theme";
import { yupResolver } from "mantine-form-yup-resolver";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { BusinessApi } from "@/apis";
import { UserWarehouseChips } from "@/features/warehouse-staff";
import type { IGeneral } from "@/models/General";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { message } from "@/utils/message";
import { FormSkeleton } from "../components/FormSkeleton";
import { renderName } from "@/utils/render-name";
import { conflictingCodes, groupRoles, isRoleLocked, roleCodeOf, roleLabel, warnUnexpectedRoles, type RoleLike } from "./role-groups";
import classes from "../business.module.css";

export type StaffFormValues = {
  userId: string;
  accessLevel: string;
  roleIds: string[];
};

export const EMPTY_STAFF_FORM: StaffFormValues = { userId: "", accessLevel: "", roleIds: [] };

type StaffOption = {
  id: string;
  firstName: string;
  lastName: string;
  avatar?: string | null;
  enabled: boolean;
};

type RoleMeta = RoleLike & { id: string; name: string; roleType?: string };

type Props = Readonly<{
  opened: boolean;
  businessId: string;
  /** Values of the staff row being edited; null when adding. */
  editing: StaffFormValues | null;
  onClose: () => void;
  /** Saved: the table reloads. */
  onSaved: () => void;
}>;

export function StaffModal({ opened, businessId, editing, onClose, onSaved }: Props) {
  const { t } = useTranslation("staff");
  const { t: tCommon } = useTranslation("common");
  const accessLevels = useSelector((state: { general: IGeneral }) => state.general.accessLevels);
  const roles = useSelector((state: { general: IGeneral }) => state.general.roles) as unknown as RoleMeta[];
  const isDehubFinance = useSelector((state: { general: IGeneral }) => state.general.isDehubFinance);
  const isDehubManager = useSelector((state: { general: IGeneral }) => state.general.isDehubManager);

  const schema = useMemo(
    () =>
      yup.object({
        userId: yup.string().required(tCommon("required")),
        accessLevel: yup.string().required(tCommon("required")),
        roleIds: yup.array().of(yup.string().required()).min(1, tCommon("required")).required(tCommon("required")),
      }),
    [tCommon],
  );
  const form = useForm<StaffFormValues>({ initialValues: EMPTY_STAFF_FORM, validate: yupResolver(schema) });
  const [staffs, setStaffs] = useState<StaffOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // The staff list request failed: the form would offer nobody to pick, so an error with a retry shows instead.
  const [staffError, setStaffError] = useState<string | null>(null);
  const [staffAttempt, setStaffAttempt] = useState(0);

  // The modal is reused: each time it opens it starts from the row being edited, or from empty.
  useEffect(() => {
    if (!opened) return;
    const start = editing ?? EMPTY_STAFF_FORM;
    form.setInitialValues(start);
    form.setValues(start);
    form.clearErrors();
    setConflictNote(null);
    let cancelled = false;
    setLoading(true);
    setStaffError(null);
    BusinessApi.users(businessId)
      .then((res: StaffOption[]) => {
        if (!cancelled) setStaffs(res ?? []);
      })
      .catch((error: { message?: string }) => {
        if (cancelled) return;
        setStaffs([]);
        setStaffError(error?.message ?? t("msg.userFetchError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, businessId, editing, staffAttempt]);

  const isEdit = editing !== null;
  const selectedCodes = useMemo(
    () => new Set((roles ?? []).filter((role) => form.values.roleIds.includes(role.id)).map(roleCodeOf)),
    [roles, form.values.roleIds],
  );
  const groups = useMemo(() => {
    const grouped = groupRoles(roles ?? []);
    warnUnexpectedRoles(roles ?? [], grouped);
    return grouped;
  }, [roles]);
  const [conflictNote, setConflictNote] = useState<string | null>(null);

  // Picking a role that cannot sit with a selected one drops the selected one and says so; a
  // selected pill is never disabled, so the user can always deselect it.
  const toggleRole = (role: RoleMeta) => {
    setConflictNote(null);
    if (form.values.roleIds.includes(role.id)) {
      form.setFieldValue("roleIds", form.values.roleIds.filter((r) => r !== role.id));
      return;
    }
    const clashing = conflictingCodes(roleCodeOf(role), selectedCodes);
    const dropped = (roles ?? []).filter(
      (other) => form.values.roleIds.includes(other.id) && clashing.includes(roleCodeOf(other)),
    );
    // A role the user may not remove is never dropped silently: the new pick is refused instead.
    const protectedRoles = dropped.filter((other) => isRoleLocked(other, { isDehubManager, isDehubFinance }));
    if (protectedRoles.length > 0) {
      setConflictNote(t("user.roleBlocked", { role: roleLabel(role, true), kept: protectedRoles.map((d) => roleLabel(d, true)).join(", ") }));
      return;
    }
    form.setFieldValue("roleIds", [...form.values.roleIds.filter((id) => !dropped.some((d) => d.id === id)), role.id]);
    if (dropped.length > 0) {
      setConflictNote(t("user.roleConflict", { removed: dropped.map((d) => roleLabel(d, true)).join(", "), role: roleLabel(role, true) }));
    }
  };

  const handleSubmit = async (values: StaffFormValues) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await BusinessApi.addUser({
        userId: values.userId,
        roleIds: values.roleIds,
        accessLevel: values.accessLevel,
        businessId,
      });
      if (res?.success) {
        message.success(t("msg.userCreateSuccess"));
        onSaved();
        onClose();
      }
    } catch (error: unknown) {
      // stay open: the user keeps what they picked and can retry
      message.error((error as { message?: string })?.message ?? t("msg.genericError"));
    } finally {
      setSaving(false);
    }
  };

  const showForm = !staffError && !(loading && staffs.length === 0);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="lg"
      size={640}
      title={isEdit ? t("user.editTitle") : t("user.addTitle")}
      classNames={{ content: classes.tokens }}
    >
      <Box component="form" noValidate onSubmit={form.onSubmit(handleSubmit)} pos="relative" className={classes.modalBody}>
        <LoadingOverlay visible={saving} zIndex={201} />
        {loading && staffs.length === 0 ? <FormSkeleton /> : null}
        {staffError ? (
          <LoadError
            title={t("user.loadErrorTitle")}
            message={staffError}
            retryLabel={tCommon("actions.retry")}
            onRetry={() => setStaffAttempt((n) => n + 1)}
          />
        ) : null}
        {showForm ? (
        <>
        <Box className={classes.modalGrid}>
          <Select
            required
            label={t("user.selectStaff")}
            placeholder={t("user.selectStaff")}
            searchable
            disabled={isEdit}
            data={staffs.map((staff) => ({
              label: renderName(staff),
              value: staff.id,
              disabled: !staff.enabled && staff.id !== editing?.userId,
            }))}
            {...form.getInputProps("userId")}
          />
          <Select
            required
            label={t("user.accessLevel")}
            placeholder={t("user.accessLevelPlaceholder")}
            data={(accessLevels ?? []).map((item) => ({ label: item.name, value: item.code }))}
            {...form.getInputProps("accessLevel")}
          />
        </Box>

        <Box>
          <Box className={classes.capsRow}>
            <Text component="span" fz={14} fw={600} c="gray.8">
              {t("user.selectRole")}
              <Text component="span" aria-hidden="true">
                {" *"}
              </Text>
            </Text>
            <Text component="span" className={classes.hint}>
              {t("user.picked", { count: form.values.roleIds.length })}
            </Text>
          </Box>
          {conflictNote ? (
            <Text component="span" display="block" fz={14} c="gray.8" role="status">
              {conflictNote}
            </Text>
          ) : null}
          {form.errors.roleIds ? (
            <Text component="span" fz={14} c="red.8">
              {form.errors.roleIds}
            </Text>
          ) : null}
        </Box>

        {groups.map((group) => (
          <Box key={group.id} component="fieldset" className={classes.roleGroup}>
            <Box component="legend" className={classes.roleLegend}>
              {t(`roleGroups.${group.id}`)}
            </Box>
            {group.id === "dehub" ? (
              <Text component="span" className={classes.hint}>
                {t("roleGroups.dehubNote")}
              </Text>
            ) : null}
            <Box className={classes.rolePills}>
              {group.roles.map((role) => {
                const selected = form.values.roleIds.includes(role.id);
                const locked = isRoleLocked(role, { isDehubManager, isDehubFinance });
                // Two different reasons: no permission (unavailable) vs. would replace a selected role (still pickable).
                const clashing = selected || locked
                  ? []
                  : (roles ?? []).filter(
                      (other) =>
                        form.values.roleIds.includes(other.id) &&
                        conflictingCodes(roleCodeOf(role), selectedCodes).includes(roleCodeOf(other)),
                    );
                const pill = (
                  <UnstyledButton
                    type="button"
                    aria-pressed={selected}
                    className={classes.rolePill}
                    onClick={() => toggleRole(role)}
                  >
                    {roleLabel(role)}
                  </UnstyledButton>
                );
                if (locked) {
                  return (
                    <ReasonTooltip key={role.id} reason={t("user.roleLocked")}>
                      {pill}
                    </ReasonTooltip>
                  );
                }
                if (clashing.length > 0) {
                  return (
                    <Tooltip
                      key={role.id}
                      label={t("user.roleConflictHint", { removed: clashing.map((c) => roleLabel(c, true)).join(", ") })}
                      withArrow
                      events={{ hover: true, focus: true, touch: true }}
                    >
                      {pill}
                    </Tooltip>
                  );
                }
                return <Fragment key={role.id}>{pill}</Fragment>;
              })}
            </Box>
          </Box>
        ))}

        {isEdit && form.values.userId ? <UserWarehouseChips userId={form.values.userId} /> : null}
        </>
        ) : null}

        <Box className={classes.modalFoot}>
          <Button variant="default" type="button" onClick={onClose}>
            {tCommon("actions.cancel")}
          </Button>
          <Button color="brand" type="submit" loading={saving} disabled={loading || !showForm}>
            {isEdit ? tCommon("actions.save") : tCommon("actions.add")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
